<template>
  <b-modal
    id="modal-participant-list"
    ref="modalParticipantList"
    title="Manage Participants"
    size="xl"
    hide-footer
    @hidden="resetModal">
    <div v-if="selectedBatch">
      <!-- Batch Info Header -->
      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h5 class="mb-2">{{ selectedCourse?.title }} - {{ formatBatchLabel(selectedBatch) }}</h5>
            <div class="d-flex gap-2 align-items-center flex-wrap">
              <span class="badge" :class="getBatchStatusClass(currentBatch.batch_status)">
                {{ currentBatch.batch_status }}
              </span>
              <span v-if="currentBatch.quota_status" class="badge badge-soft-danger">
                {{ currentBatch.quota_status }}
              </span>
              <span class="text-muted">•</span>
              <small class="text-muted">
                {{ getEnrolledCount(currentBatch) }}/{{ getQuota(currentBatch) ?? '-' }} peserta · sisa {{ getRemainingQuotaWithDrafts(currentBatch) ?? '-' }}
              </small>
            </div>
          </div>
          <div>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              @click="showAddParticipantModal"
              :disabled="!canAddParticipant">
              <i class="fe fe-plus me-2"></i>
              Add Participant
            </button>
          </div>
        </div>
      </div>

      <!-- Participants Table -->
      <div v-if="enrolledParticipants.length === 0" class="text-center text-muted py-4">
            Belum ada peserta yang dienroll.
      </div>
      <vue-good-table
        v-else
        :columns="participantColumns"
        :rows="enrolledParticipants"
        :pagination-options="{ enabled: true }"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'no'">
            {{ props.index + 1 }}
          </span>
          <span v-else-if="props.column.field === 'nik'">
            <span class="badge badge-soft-primary">{{ props.row.nik }}</span>
          </span>
          <span v-else-if="props.column.field === 'fullname'">
            {{ props.row.fullname || props.row.full_name || '-' }}
          </span>
          <span v-else-if="props.column.field === 'training_type'">
            <span
              class="badge"
              :class="props.row.is_mandatory ? 'badge-soft-danger' : 'badge-soft-info'">
              {{ props.row.is_mandatory ? 'Mandatory' : 'Development' }}
            </span>
          </span>
          <span v-else-if="props.column.field === 'company'">
            <small class="text-muted">{{ props.row.company || props.row.company_name || '-' }}</small>
          </span>
          <span v-else-if="props.column.field === 'submitted'">
            <span v-if="props.row.status">
              <span class="badge" :class="getParticipantStatusClass(props.row.status)">
                {{ getParticipantStatusLabel(props.row.status) }}
              </span>
            </span>
            <span v-else-if="props.row.submitted">
              <span class="badge badge-soft-primary">Ready to Submit</span>
            </span>
            <span v-else class="badge badge-soft-secondary">Draft</span>
          </span>
          <span v-else-if="props.column.field === 'actions'">
            <button
              v-if="isDraftParticipant(props.row)"
              type="button"
              class="btn btn-sm btn-outline-danger"
              @click="removeParticipant(props.row)"
              title="Remove">
              <i class="fe fe-trash-2"></i>
            </button>
            <span v-else class="text-muted">-</span>
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>

      <!-- Action Buttons -->
      <div class="mt-4 d-flex gap-2 justify-content-end">
        <button 
          type="button" 
          class="btn btn-secondary"
          @click="closeModal">
          Close
        </button>
        <button 
          type="button" 
          class="btn btn-success"
          :disabled="!canSubmitParticipants"
          @click="submitParticipants">
          <span v-if="isSubmitting">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Mengirim...
          </span>
          <span v-else>
            <i class="fe fe-check me-2"></i>
            Submit Peserta ({{ submitableParticipants.length }})
          </span>
        </button>
      </div>
    </div>

    <!-- Modal Add Participant -->
    <b-modal
      id="modal-add-participant"
      ref="modalAddParticipant"
      title="Add Participant"
      @ok="addParticipant">
      <div class="form-group">
        <label>Select Participant:</label>
        <v-select
          v-model="selectedParticipant"
          :options="availableParticipantsOptions"
          label="label"
          placeholder="Cari nama atau NIK..."
          :reduce="option => option.value"
          :filterable="true">
          <template #option="{ value }">
            <div>
              <strong>{{ value.fullname }}</strong>
              <br>
              <small>{{ value.nik }} - {{ value.company }}</small>
              <small v-if="findDraftBatch(value)" class="d-block text-info">
                Draft di {{ formatBatchLabel(findDraftBatch(value)) }} · akan dipindahkan
              </small>
            </div>
          </template>
        </v-select>
      </div>
      
      <div v-if="selectedParticipant" class="alert alert-info mt-3">
        <h6>Selected Participant:</h6>
        <p class="mb-1"><strong>Name:</strong> {{ selectedParticipant.fullname }}</p>
        <p class="mb-1"><strong>NIK:</strong> {{ selectedParticipant.nik }}</p>
        <p class="mb-1"><strong>Position:</strong> {{ selectedParticipant.position_level }}</p>
        <p class="mb-0"><strong>Company:</strong> {{ selectedParticipant.company }}</p>
        <p v-if="selectedParticipantDraftBatch" class="mb-0 mt-2">
          Masih draft di {{ formatBatchLabel(selectedParticipantDraftBatch) }}. Peserta akan dipindahkan ke {{ formatBatchLabel(currentBatch) }}.
        </p>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { normalizeErrorMessage } from '@/utils/error-message';
import { formatBatchLabel } from '@/utils/batchIdentity';
import { getAvailableParticipantOptions } from '@/utils/mitraEnrollmentParticipants';
import {
  canEnroll,
  getEnrolledCount,
  getQuota,
  getRemainingQuota,
  getRemainingQuotaWithDrafts,
  isQuotaFull
} from '@/utils/enrollmentQuota';

export default {
  name: 'ModalParticipantList',
  components: {
    VueGoodTable
  },
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    selectedBatch: {
      type: Object,
      default: null
    },
    batchIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      selectedParticipant: null,
      isSubmitting: false,
      participantColumns: [
        { label: 'No', field: 'no', width: '40px' },
        { label: 'NIK', field: 'nik' },
        { label: 'Full Name', field: 'fullname' },
        { label: 'Type', field: 'training_type' },
        { label: 'Position', field: 'position_level' },
        { label: 'Company', field: 'company' },
        { label: 'Approval Status', field: 'submitted' },
        { label: 'Actions', field: 'actions', width: '80px' }
      ]
    };
  },
  computed: {
    ...mapGetters('mitraEnrollment', ['availableParticipants', 'batches']),

    currentBatch() {
      return this.batches[this.batchIndex] || this.selectedBatch || {};
    },
    
    enrolledParticipants() {
      // Get from store to ensure reactivity
      return this.currentBatch.company_participants || [];
    },
    
    pendingParticipantsCount() {
      // Count participants yang belum di-submit (submitted = false)
      return this.submitableParticipants.length;
    },

    submitableParticipants() {
      return this.enrolledParticipants.filter(p => this.isDraftParticipant(p));
    },

    remainingSeats() {
      return getRemainingQuota(this.currentBatch);
    },

    selectedParticipantDraftBatch() {
      if (!this.selectedParticipant) return null;
      return this.findDraftBatch(this.selectedParticipant);
    },

    canAddParticipant() {
      if (!canEnroll(this.currentBatch)) return false;
      if (this.remainingSeats == null) return true;
      return this.pendingParticipantsCount < this.remainingSeats;
    },

    canSubmitParticipants() {
      if (this.isSubmitting || this.submitableParticipants.length === 0) return false;
      if (isQuotaFull(this.currentBatch)) return false;
      if (this.remainingSeats == null) return true;
      return this.submitableParticipants.length <= this.remainingSeats;
    },
    
    // Recommendations live in batches, so keep drafts searchable even when
    // the API's unassigned participant list does not include them.
    availableParticipantsOptions() {
      return getAvailableParticipantOptions({
        batches: this.batches,
        currentBatch: this.currentBatch,
        enrolledParticipants: this.enrolledParticipants,
        availableParticipants: this.availableParticipants,
        isDraftParticipant: this.isDraftParticipant
      });
    }
  },
  methods: {
    ...mapActions('mitraEnrollment', {
      addParticipantToBatch: 'addParticipantToBatch',
      removeParticipantFromBatch: 'removeParticipantFromBatch',
      submitParticipantsAction: 'submitParticipants'
    }),
    formatBatchLabel,
    findDraftBatch(participant) {
      return this.batches.find(batch =>
        batch.training_event_id !== this.currentBatch.training_event_id &&
        (batch.company_participants || []).some(item =>
          item.nik === participant.nik && this.isDraftParticipant(item)
        )
      ) || null;
    },
    
    resetModal() {
      this.selectedParticipant = null;
    },
    
    closeModal() {
      this.$refs.modalParticipantList.hide();
    },
    
    getEnrolledCount,
    getQuota,
    getRemainingQuota,
    getRemainingQuotaWithDrafts,

    showAddParticipantModal() {
      if (!this.canAddParticipant) return;
      this.selectedParticipant = null;
      this.$refs.modalAddParticipant.show();
    },
    
    async addParticipant(event) {
      event.preventDefault();
      if (!this.canAddParticipant) {
        this.$swal({
          title: 'Quota Full',
          text: 'Quota sudah penuh. Tidak dapat menambahkan peserta baru ke batch ini.',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      if (!this.selectedParticipant) {
        this.$swal({
          title: 'Error',
          text: 'Pilih peserta terlebih dahulu.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }
      
      try {
        const participant = this.selectedParticipant;
        const result = await this.addParticipantToBatch({
          batchIndex: this.batchIndex,
          participant: {
            nik: participant.nik,
            tna_id: participant.tna_id,
            fullname: participant.fullname || participant.full_name || '',
            company: participant.company,
            position_level: participant.position_level,
            is_mandatory: participant.is_mandatory
          }
        });
        this.$refs.modalAddParticipant.hide();
        this.$swal({
          title: 'Success',
          text: result.movedFrom
            ? `Peserta dipindahkan dari ${formatBatchLabel(result.movedFrom)} ke ${formatBatchLabel(this.currentBatch)}.`
            : 'Peserta berhasil ditambahkan.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757',
          timer: 1500
        });
        this.selectedParticipant = null;
      } catch (error) {
        this.$swal({
          title: 'Peserta belum dapat dipindahkan',
          text: normalizeErrorMessage(error),
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    removeParticipant(participant) {
      if (!this.isDraftParticipant(participant)) return;

      this.$swal({
        title: 'Hapus Peserta?',
        html: `Anda yakin ingin menghapus <strong>${participant.fullname}</strong> dari batch ini?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya, Hapus',
        confirmButtonColor: '#e63757',
        cancelButtonText: 'Batal',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          // Remove from batch (local state)
          this.removeParticipantFromBatch({
            batchIndex: this.batchIndex,
            participantNik: participant.nik
          });
          
          this.$swal({
            title: 'Success',
            text: 'Peserta berhasil dihapus.',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757',
            timer: 1500
          });
        }
      });
    },
    
    async submitParticipants() {
      const participantsToSubmit = this.submitableParticipants;
      if (participantsToSubmit.length === 0) return;
      if (!this.canSubmitParticipants) {
        this.$swal({
          title: 'Quota Full',
          text: 'Quota sudah penuh. Tidak dapat menambahkan peserta baru ke batch ini.',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }

      const result = await this.$swal({
        title: 'Submit Peserta?',
        html: `Anda akan submit <strong>${participantsToSubmit.length}</strong> peserta baru untuk batch ini.<br/>Peserta yang sudah masuk flow approval akan dilewati.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya, Submit',
        confirmButtonColor: '#00c9a7',
        cancelButtonText: 'Batal',
        reverseButtons: true
      });
      
      if (result.isConfirmed) {
        this.isSubmitting = true;
        try {
          // Prepare data untuk submit
          const tnas = participantsToSubmit.map(p => p.tna_id);
          
          await this.submitParticipantsAction({
            batchId: this.selectedBatch.training_event_id,
            data: { approved: tnas }
          });
          
          this.$swal({
            title: 'Success',
            text: 'Peserta berhasil disubmit.',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
          
          // Close modal and refresh batch
          this.closeModal();
          this.$emit('refresh-batch');
        } catch (error) {
          // Extract error message from response
          const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               error.message || 
                               'Peserta belum berhasil dikirim';
          
          this.$swal({
            title: 'Error',
            text: normalizeErrorMessage(errorMessage),
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
        } finally {
          this.isSubmitting = false;
        }
      }
    },
    
    getBatchStatusClass(status) {
      const classes = {
        'upcoming': 'badge-soft-warning',
        'ongoing': 'badge-soft-success',
        'completed': 'badge-soft-info',
        'cancelled': 'badge-soft-danger'
      };
      return classes[status?.toLowerCase()] || 'badge-soft-secondary';
    },

    getParticipantStatusLabel(status) {
      const labels = {
        'Waiting for PJO Approval': 'Approval PJO',
        'Waiting for Admin HSE Approval': 'Approval HSE Training',
        'Approved': 'Enrolled',
        'Rejected': 'Rejected',
        'Waiting for Cancellation Approval': 'Unenroll Approval',
        'Waiting for HSE Training Unenrollment Approval': 'Unenroll Approval HSE Training',
        'Cancelled': 'Cancelled',
        'Completed': 'Completed',
        'Not Completed': 'Not Completed'
      };
      return labels[status] || status || 'Draft';
    },

    getParticipantStatusClass(status) {
      const classes = {
        'Waiting for PJO Approval': 'badge-soft-warning',
        'Waiting for Admin HSE Approval': 'badge-soft-warning',
        'Approved': 'badge-soft-success',
        'Rejected': 'badge-soft-danger',
        'Waiting for Cancellation Approval': 'badge-soft-warning',
        'Waiting for HSE Training Unenrollment Approval': 'badge-soft-info',
        'Cancelled': 'badge-soft-danger',
        'Completed': 'badge-soft-success',
        'Not Completed': 'badge-soft-danger'
      };
      return classes[status] || 'badge-soft-primary';
    },

    isDraftParticipant(participant) {
      return !participant?.status && !participant?.submitted;
    }
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

.badge-soft-primary {
  color: #2c7be5;
  background-color: rgba(44, 123, 229, 0.1);
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}
</style>
